import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { COUNTRIES } from "../../utils/countries";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import authService from "../../services/authService";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Doctor Specialization Options (English) - Alphabetically sorted
const SPECIALIZATIONS = [
  "Allergy and Immunology",
  "Anesthesiology",
  "Cardiology",
  "Dermatology",
  "Emergency Medicine",
  "Endocrinology",
  "Family Medicine",
  "Gastroenterology",
  "Hematology",
  "Infectious Disease",
  "Internal Medicine",
  "Nephrology",
  "Neurology",
  "Obstetrics and Gynecology",
  "Oncology",
  "Ophthalmology",
  "Orthopedics",
  "Otolaryngology",
  "Pathology",
  "Pediatrics",
  "Physical Medicine and Rehabilitation",
  "Psychiatry",
  "Pulmonology",
  "Radiology",
  "Rheumatology",
  "Surgery",
  "Urology",
  "Other",
];

/**
 * 🎯 DoctorInvitationComplete
 * Davet linki üzerinden doktor kaydını tamamlama sayfası
 * Route: /invite/doctor/:token
 */
export default function DoctorInvitationComplete() {
  const { token } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Token'dan çıkarılan bilgiler
  const [invitationInfo, setInvitationInfo] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    password: "",
    confirmPassword: "",
    specialization: "",
    phone_number: "",
    nationality: "",
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Token doğrulama ve bilgileri çıkarma
    const verifyToken = async () => {
      try {
        const payload = await authService.verifyInvitationToken(token);
        setInvitationInfo({
          email: payload.email,
          role: payload.role,
          tenant_id: payload.tenant_id,
        });
        setLoading(false);
      } catch (err) {
        setError("Invalid or expired invitation link.");
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setError("Invitation link not found.");
      setLoading(false);
    }
  }, [token]);

  const capitalizeWords = (str) => {
    if (!str) return str;
    // Önce tüm string'i küçük harfe çevir, sonra kelime başlarını büyük yap
    return str.trim().toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    // Capitalize name and surname when user leaves the field
    if ((name === "name" || name === "surname") && value.trim()) {
      const capitalizedValue = capitalizeWords(value);
      setFormData((prev) => ({ ...prev, [name]: capitalizedValue }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "First name is required";
    }

    if (!formData.surname.trim()) {
      errors.surname = "Last name is required";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Password confirmation is required";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!formData.specialization.trim()) {
      errors.specialization = "Specialization is required";
    }

    if (!formData.phone_number.trim()) {
      errors.phone_number = "Phone number is required";
    } else if (!/^[0-9+\-() ]*$/.test(formData.phone_number)) {
      errors.phone_number = "Invalid phone number format";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Form validation
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);

    try {
      // Capitalize name and surname before submitting
      const capitalizedName = capitalizeWords(formData.name);
      const capitalizedSurname = capitalizeWords(formData.surname);
      
      await authService.completeDoctorInvitation({
        token,
        name: capitalizedName,
        surname: capitalizedSurname,
        password: formData.password,
        specialization: formData.specialization,
        phone_number: formData.phone_number,
        nationality: formData.nationality || null,
      });

      setSuccess(true);

      setTimeout(() => {
        navigate("/individual-login");
      }, 2000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        "An error occurred while completing registration. Please try again.";
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.primary[400],
        }}
      >
        <CircularProgress size={60} sx={{ color: colors.greenAccent[500] }} />
      </Box>
    );
  }

  if (error && !invitationInfo) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.primary[400],
          padding: 3,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            maxWidth: 500,
            padding: 4,
            backgroundColor: colors.primary[500],
            borderRadius: 3,
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate("/")}
            sx={{
              backgroundColor: colors.blueAccent[600],
              "&:hover": { backgroundColor: colors.blueAccent[700] },
            }}
          >
            Return to Home Page
          </Button>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.primary[400],
          padding: 3,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            maxWidth: 500,
            padding: 5,
            backgroundColor: colors.primary[500],
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 80, color: colors.greenAccent[500], mb: 2 }}
          />
          <Typography
            variant="h3"
            sx={{ mb: 2, color: colors.greenAccent[500], fontWeight: "bold" }}
          >
            Registration Successful!
          </Typography>
          <Typography variant="body1" sx={{ color: colors.grey[100] }}>
            Your account has been successfully created. Redirecting to login page...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: colors.primary[400],
        padding: 3,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 600,
          width: "100%",
          padding: 4,
          backgroundColor: colors.primary[500],
          borderRadius: 3,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: "bold",
              background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            MediFormTrack
          </Typography>
          <Typography variant="h4" sx={{ color: colors.grey[100], mb: 1 }}>
            {invitationInfo?.role === "admin"
              ? "Admin Doctor Registration"
              : "Doctor Registration"}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[300] }}>
            Please complete your information to create your account
          </Typography>
        </Box>

        {/* Email Info */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            backgroundColor: colors.primary[600],
            border: `1px solid ${colors.greenAccent[700]}`,
          }}
        >
          <Typography variant="body2" sx={{ color: colors.grey[300], mb: 0.5 }}>
            Invited Email:
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: colors.greenAccent[400], fontWeight: "bold" }}
          >
            {invitationInfo?.email}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: colors.grey[400], mt: 0.5, display: "block" }}
          >
            Role: {invitationInfo?.role === "admin" ? "Admin Doctor" : "Doctor"}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Registration Form */}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Name */}
            <TextField
              fullWidth
              label="First Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(formErrors.name)}
              helperText={formErrors.name}
              variant="filled"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: colors.greenAccent[500] }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Surname */}
            <TextField
              fullWidth
              label="Last Name *"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(formErrors.surname)}
              helperText={formErrors.surname}
              variant="filled"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: colors.greenAccent[500] }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Specialization */}
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                <WorkIcon sx={{ color: colors.greenAccent[500], fontSize: 20 }} />
              </Box>
              <FormControl
                fullWidth
                variant="filled"
                error={Boolean(formErrors.specialization)}
              >
                <InputLabel
                sx={{
                    color: colors.grey[300],
                    ml: 5,
                    "&.Mui-focused": {
                      color: colors.greenAccent[400],
                    },
                    "&.MuiInputLabel-shrink": {
                      ml: 5,
                  },
                }}
              >
                  Specialization *
                </InputLabel>
                <Select
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleChange}
                  IconComponent={ExpandMoreIcon}
                  sx={{
                    "& .MuiSelect-select": {
                      paddingLeft: "48px",
                      color: formData.specialization
                        ? colors.grey[100]
                        : colors.grey[400],
                    },
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[600],
                      "&:hover": {
                        backgroundColor: colors.primary[500],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[500],
                      },
                    },
                    "& .MuiFilledInput-input": {
                      paddingLeft: "48px",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: colors.primary[600],
                        borderRadius: 2,
                        mt: 1,
                        maxHeight: 300,
                        "& .MuiMenuItem-root": {
                          color: colors.grey[100],
                          px: 2,
                          py: 1.5,
                          "&:hover": {
                            backgroundColor: colors.greenAccent[700],
                            color: "white",
                          },
                          "&.Mui-selected": {
                            backgroundColor: colors.greenAccent[800],
                            color: "white",
                            "&:hover": {
                              backgroundColor: colors.greenAccent[700],
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select specialization</em>
                  </MenuItem>
                  {SPECIALIZATIONS.map((specialization) => (
                    <MenuItem key={specialization} value={specialization}>
                      {specialization}
                    </MenuItem>
                  ))}
                </Select>
              {formErrors.specialization && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.redAccent[500],
                    mt: 0.5,
                    ml: 1.75,
                      fontSize: "0.75rem",
                  }}
                >
                  {formErrors.specialization}
                </Typography>
              )}
              </FormControl>
            </Box>

            {/* Phone Number */}
            <TextField
              fullWidth
              label="Phone Number *"
              name="phone_number"
              value={formData.phone_number}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9+\-() ]*$/.test(value)) {
                  handleChange(e);
                }
              }}
              error={Boolean(formErrors.phone_number)}
              helperText={formErrors.phone_number}
              variant="filled"
              inputProps={{
                inputMode: "tel",
                pattern: "[0-9+\\-() ]*",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: colors.greenAccent[500] }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Nationality */}
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                <PersonIcon sx={{ color: colors.greenAccent[500], fontSize: 20 }} />
              </Box>
              <FormControl
                fullWidth
                variant="filled"
              >
                <InputLabel
                  sx={{
                    color: colors.grey[300],
                    ml: 5,
                    "&.Mui-focused": {
                      color: colors.greenAccent[400],
                    },
                    "&.MuiInputLabel-shrink": {
                      ml: 5,
                    },
                  }}
                >
                  Nationality
                </InputLabel>
                <Select
                  name="nationality"
                  value={formData.nationality || ""}
                  onChange={handleChange}
                  IconComponent={ExpandMoreIcon}
                  sx={{
                    "& .MuiSelect-select": {
                      paddingLeft: "48px",
                      color: formData.nationality
                        ? colors.grey[100]
                        : colors.grey[400],
                    },
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[600],
                      "&:hover": {
                        backgroundColor: colors.primary[500],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[500],
                      },
                    },
                    "& .MuiFilledInput-input": {
                      paddingLeft: "48px",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: colors.primary[600],
                        borderRadius: 2,
                        mt: 1,
                        maxHeight: 300,
                        "& .MuiMenuItem-root": {
                          color: colors.grey[100],
                          px: 2,
                          py: 1.5,
                          "&:hover": {
                            backgroundColor: colors.greenAccent[700],
                            color: "white",
                          },
                          "&.Mui-selected": {
                            backgroundColor: colors.greenAccent[800],
                            color: "white",
                            "&:hover": {
                              backgroundColor: colors.greenAccent[700],
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select nationality</em>
                  </MenuItem>
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Password */}
            <TextField
              fullWidth
              label="Password *"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              error={Boolean(formErrors.password)}
              helperText={formErrors.password || "At least 6 characters"}
              variant="filled"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: colors.greenAccent[500] }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Confirm Password */}
            <TextField
              fullWidth
              label="Confirm Password *"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={Boolean(formErrors.confirmPassword)}
              helperText={formErrors.confirmPassword}
              variant="filled"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: colors.greenAccent[500] }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting}
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: "16px",
                fontWeight: "bold",
                background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[700]} 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[800]} 100%)`,
                },
                "&:disabled": {
                  background: colors.grey[700],
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Complete Registration"
              )}
            </Button>
          </Box>
        </form>

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            MediFormTrack © 2025 - Secure Registration System
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}


